import { useCallback, useEffect, useState } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import {
  encolarFoto,
  fotosEnCola,
  quitarDeCola,
  todaLaCola,
  type FotoEnCola,
} from '@/lib/campo/colaDeFotos'

import { subirFotoDeOrden } from '@/modules/ordenes-servicio/useFotosOrden'

/**
 * SUBIR LO QUE ESPERA EN LA COLA, EN CUANTO SE PUEDA.
 *
 * El motor vive a nivel de módulo, no dentro del hook: así, aunque la pantalla
 * de fotos se abra y se cierre, la subida sigue su curso y no se dispara dos
 * veces la misma foto. `enVuelo` evita que dos tandas suban la misma; `corriendo`
 * evita dos tandas a la vez.
 *
 * Se reintenta solo cuando el navegador avisa que volvió la conexión. Si una
 * foto falla —señal que se cae a mitad—, se queda en la cola y entra en la
 * próxima vuelta; nunca se pierde.
 */
const enVuelo = new Set<string>()
let corriendo = false
const oyentes = new Set<() => void>()

function avisarCambio() {
  oyentes.forEach((fn) => fn())
}

/**
 * La vuelta que está subiendo ahora. Quien llama mientras corre ESPERA esa
 * misma vuelta en vez de irse con las manos vacías: la visita de
 * certificación (0256) necesita que sus fotos hayan subido antes de
 * registrarse, y si la cola de fotos ya iba en camino por el evento
 * «online», la visita se quedaba esperando hasta el siguiente corte de red.
 */
let vueltaEnCurso: Promise<void> | null = null

export function sincronizarFotos(): Promise<void> {
  if (corriendo && vueltaEnCurso) return vueltaEnCurso
  vueltaEnCurso = subirTodo().finally(() => {
    vueltaEnCurso = null
  })
  return vueltaEnCurso
}

async function subirTodo(): Promise<void> {
  if (corriendo) return
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return

  corriendo = true
  try {
    for (const foto of await todaLaCola()) {
      if (enVuelo.has(foto.id)) continue
      enVuelo.add(foto.id)
      try {
        await subirFotoDeOrden(foto.ordenServicioId, foto.blob, {
          id: foto.id,
          hash: foto.hash,
          tomadaEn: foto.tomadaEn,
        })
        await quitarDeCola(foto.id)
        avisarCambio()
      } catch {
        // Se queda en la cola y se reintenta en la próxima vuelta.
      } finally {
        enVuelo.delete(foto.id)
      }
    }
  } finally {
    corriendo = false
  }
}

// En cuanto vuelve la señal, se vacía lo que haya esperando.
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => void sincronizarFotos())
  // 0324: y al volver a la aplicación y cada 45 s, porque la señal puede
  // volver sin que el teléfono dispare `online` (ver useColaDeAcciones).
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void sincronizarFotos()
  })
  window.setInterval(() => void sincronizarFotos(), 45_000)
}

/**
 * Lo que una orden tiene esperando subir, y cómo agregar y quitar.
 *
 * Devuelve `enCola` (las pendientes de ESTA orden) y refresca solo cuando el
 * motor avisa que algo cambió —una subió, o el técnico agregó o quitó—, para no
 * consultar IndexedDB en cada render.
 */
export function useColaDeFotos(ordenServicioId: string | null) {
  const cliente = useQueryClient()
  const [enCola, setEnCola] = useState<FotoEnCola[]>([])

  const refrescar = useCallback(async () => {
    if (!ordenServicioId) {
      setEnCola([])
      return
    }
    setEnCola(await fotosEnCola(ordenServicioId))
  }, [ordenServicioId])

  useEffect(() => {
    void refrescar()

    const alCambiar = () => {
      void refrescar()
      // Una que acaba de subir ya está en el servidor: se relee la lista.
      void cliente.invalidateQueries({ queryKey: ['fotos-orden', ordenServicioId] })
    }
    oyentes.add(alCambiar)

    // Al abrir la pantalla, se intenta vaciar lo que haya quedado de antes.
    void sincronizarFotos()

    return () => {
      oyentes.delete(alCambiar)
    }
  }, [refrescar, ordenServicioId, cliente])

  const agregar = useCallback(
    async (foto: FotoEnCola) => {
      await encolarFoto(foto)
      await refrescar()
      // Que se enteren TODOS los que miran esta orden, no solo quien tomó la
      // foto: el paso «Fotos» del registro de la visita y el «Falta la foto»
      // del cotizador leen la misma cola desde otro componente, y se quedaban
      // en cero con la foto ya guardada (lo destapó la prueba sin señal, 0256).
      avisarCambio()
      // Si hay señal, sube en el acto; si no, queda esperando.
      void sincronizarFotos()
    },
    [refrescar],
  )

  const quitar = useCallback(
    async (id: string) => {
      await quitarDeCola(id)
      await refrescar()
      avisarCambio()
    },
    [refrescar],
  )

  return { enCola, agregar, quitar }
}
